<script setup lang="ts">
/**
 * Transfer of Shares — 문서 패키지를 작성 흐름 단계(스텝퍼)로 렌더 (OTC 미러링).
 * · 단계 = task 에 **연결된 문서만** 표시(ctx.docKindEnabled) → with SC(4단계) / without SC(Waiver·Share Certificate 미연결 → 2단계) 자동 분기.
 * · Section 105 · Share Certificate 는 **양도 1건 = 1부** → 양도 건수만큼 개별 렌더.
 * · 모든 문서 마운트 유지(v-show), 필드 키 공유(ctx). 각 문서 = A4 한 장.
 */
import { ref, nextTick, computed, inject, watch } from 'vue';
import type { Component } from 'vue';
import { TRANSFER_STAGES } from './transferFields';
import { TRANSFER_DOC_CTX } from './transferContext';
import TransferWaiverDoc from './TransferWaiverDoc.vue';
import TransferSection105Doc from './TransferSection105Doc.vue';
import TransferDcrDoc from './TransferDcrDoc.vue';
import TransferShareCertDoc from './TransferShareCertDoc.vue';

const ctx = inject(TRANSFER_DOC_CTX)!;

// 단일-문서 스테이지(dcr/waiver) → 컴포넌트·라벨·아이콘. section105·sharecert 는 반복부에 따라 ×N 확장(아래 special-case).
const SINGLE_DOC: Record<'dcr' | 'waiver', { icon: string; label: string; comp: Component }> = {
  dcr: { icon: '⚖️', label: "Directors' Circular Resolution (Transfer of Shares)", comp: TransferDcrDoc },
  waiver: { icon: '📝', label: 'Waiver of Pre-emption Rights (Transfer of Shares)', comp: TransferWaiverDoc },
};

const perRows = computed(() => Array.from({ length: Math.max(1, ctx.transferCount.value) }, (_, i) => i + 1));
function transferorNameOf(n: number): string { return String(ctx.valueOf(`tr${n}_transferor_name`) || '').trim(); }
function transfereeNameOf(n: number): string { return String(ctx.valueOf(`tr${n}_transferee_name`) || '').trim(); }

/** 연결된(활성) 단계만 — 배열 순서대로. */
const visibleStages = computed(() => TRANSFER_STAGES.filter((st) => ctx.docKindEnabled(st.kind)));
const totalStages = computed(() => visibleStages.value.length);

const view = computed(() => {
  let n = 0; let disp = 0;
  return visibleStages.value.map((st) => {
    disp += 1;
    if (st.kind === 'section105') {
      // 양도 1건 = Section 105 1부
      const docs = perRows.value.map((row) => ({
        kind: 's105' as const, index: row, comp: TransferSection105Doc as Component, icon: '📄',
        label: `Form of Transfer #${row}` + (transfereeNameOf(row) ? ` — ${transfereeNameOf(row)}` : ''), no: (n += 1),
      }));
      return { ...st, disp, docs };
    }
    if (st.kind === 'sharecert') {
      // 양수인 1명 = Share Certificate 1부
      const docs = perRows.value.map((row) => ({
        kind: 'sc' as const, index: row, comp: TransferShareCertDoc as Component, icon: '📜',
        label: `Share Certificate #${row}` + (transfereeNameOf(row) ? ` — ${transfereeNameOf(row)}` : ''), no: (n += 1),
      }));
      return { ...st, disp, docs };
    }
    const s = SINGLE_DOC[st.kind];
    const docs = [{ kind: 'comp' as const, index: 0, comp: s.comp, icon: s.icon, label: s.label, no: (n += 1) }];
    return { ...st, disp, docs };
  });
});
const totalDocs = computed(() => view.value.reduce((s, st) => s + st.docs.length, 0)); // kind-driven order (DCR first)

const currentStage = ref(1);
// 현재 단계가 비활성(미연결)이면 첫 활성 단계로.
watch(visibleStages, (arr) => {
  if (!arr.find((s) => s.no === currentStage.value)) currentStage.value = arr[0]?.no ?? 1;
}, { immediate: true });
const currentDisp = computed(() => {
  const idx = visibleStages.value.findIndex((s) => s.no === currentStage.value);
  return idx < 0 ? 1 : idx + 1;
});

const rootEl = ref<HTMLElement>();
function scrollToTop() {
  nextTick(() => {
    const el = rootEl.value;
    if (!el) return;
    let p: HTMLElement | null = el.parentElement;
    while (p) {
      const oy = getComputedStyle(p).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) { p.scrollTo({ top: 0, behavior: 'smooth' }); return; }
      p = p.parentElement;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}
function go(no: number) { currentStage.value = no; scrollToTop(); }
function goRel(delta: number) {
  const arr = visibleStages.value;
  const idx = arr.findIndex((s) => s.no === currentStage.value);
  const next = arr[idx + delta];
  if (next) go(next.no);
}

/** 검증 다이얼로그에서 미입력 항목 클릭 → 해당 stage 로 전환 후 필드로 스크롤 + 강조. */
function focusField(key: string, stage: number) {
  if (visibleStages.value.find((s) => s.no === stage)) currentStage.value = stage;
  nextTick(() => {
    const sel = `[data-transfer-field="${(window.CSS && CSS.escape) ? CSS.escape(key) : key}"]`;
    const els = Array.from(document.querySelectorAll(sel)) as HTMLElement[];
    const target = els.find((e) => e.offsetParent !== null) || els[0];
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('trf-flash');
    setTimeout(() => target.classList.remove('trf-flash'), 1600);
  });
}
defineExpose({ focusField });
</script>

<template>
  <div ref="rootEl" class="allot-pkg">
    <div class="stepper">
      <button
        v-for="st in view" :key="st.no"
        class="step" :class="{ active: currentStage === st.no, done: st.disp < currentDisp }"
        :title="`Stage ${st.disp} · ${st.en}`" @click="go(st.no)"
      >
        <span class="step-no">{{ st.disp < currentDisp ? '✓' : st.disp }}</span>
        <span class="step-txt"><b>Stage {{ st.disp }}</b><small>{{ st.en }}</small></span>
      </button>
    </div>

    <section v-for="st in view" :key="st.no" v-show="currentStage === st.no" class="stage">
      <div class="stage-head">
        <span class="stage-badge">Stage {{ st.disp }}</span>
        <span class="stage-t">{{ st.en }}</span>
      </div>
      <div v-for="d in st.docs" :key="`${st.no}-${d.label}`" class="pkg-doc">
        <div class="pkg-doclabel">
          <span class="ic">{{ d.icon }}</span>
          <span class="nm">{{ d.label }}</span>
          <span class="no">Doc {{ d.no }} / {{ totalDocs }}</span>
        </div>
        <div class="pkg-paper">
          <TransferSection105Doc v-if="d.kind === 's105'" :index="d.index" />
          <TransferShareCertDoc v-else-if="d.kind === 'sc'" :index="d.index" />
          <component v-else :is="d.comp" />
        </div>
      </div>
    </section>

    <div class="stage-nav">
      <button v-if="currentDisp > 1" class="nav-btn" @click="goRel(-1)">← Previous</button>
      <span class="nav-count">Stage {{ currentDisp }} / {{ totalStages }}</span>
      <button v-if="currentDisp < totalStages" class="nav-btn primary" @click="goRel(1)">Next →</button>
      <button v-else class="nav-btn primary" disabled>All stages complete ✓</button>
    </div>
  </div>
</template>

<style src="./transferDoc.css"></style>

<style scoped>
.allot-pkg { color: #111; }
.stepper { display: flex; gap: 8px; width: 100%; max-width: 900px; margin: 0 auto 18px; padding: 10px; background: #fff; border: 1px solid #e6ebf3; border-radius: 14px; box-shadow: 0 6px 18px rgba(20, 30, 60, .06); }
.step { flex: 1; display: flex; align-items: center; gap: 9px; padding: 9px 11px; border: 1px solid transparent; border-radius: 10px; background: #f6f8fc; cursor: pointer; text-align: left; transition: all .15s; }
.step:hover { background: #eef3fb; }
.step.active { background: #eaf1ff; border-color: #b9d0ff; }
.step.done { background: #eafaf1; }
.step-no { flex: none; width: 24px; height: 24px; border-radius: 50%; background: #c9d3e4; color: #fff; font-weight: 800; font-size: 12px; display: inline-flex; align-items: center; justify-content: center; }
.step.active .step-no { background: #1b64da; }
.step.done .step-no { background: #22a06b; }
.step-txt { display: flex; flex-direction: column; line-height: 1.25; min-width: 0; }
.step-txt b { font-size: 12px; color: #1f2d4d; }
.step-txt small { font-size: 10.5px; color: #7a869c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.stage { width: 100%; max-width: 210mm; margin: 0 auto; }
.stage-head { display: flex; align-items: center; gap: 10px; margin: 4px 0 14px; padding: 9px 4px; }
.stage-badge { flex: none; background: #1b64da; color: #fff; font-weight: 800; font-size: 12.5px; padding: 4px 11px; border-radius: 999px; letter-spacing: .02em; }
.stage-t { font-weight: 800; font-size: 14px; color: #1f2d4d; }
.pkg-doc { width: 100%; margin: 0 auto; }
.pkg-doc + .pkg-doc { margin-top: 30px; }
.pkg-doclabel { display: flex; align-items: center; gap: 7px; padding: 0 4px 9px; }
.pkg-doclabel .ic { font-size: 14px; line-height: 1; }
.pkg-doclabel .nm { font-size: 12px; font-weight: 800; color: #42506a; letter-spacing: .01em; }
.pkg-doclabel .no { margin-left: auto; font-size: 10.5px; font-weight: 800; color: #9aa6bd; white-space: nowrap; }
/* 각 문서 = A4 한 장 (210mm × 297mm). */
.pkg-paper { width: 210mm; min-height: 297mm; max-width: 100%; margin: 0 auto; padding: 18mm 20mm; box-sizing: border-box; background: #fff; box-shadow: 0 4px 24px rgba(20, 30, 60, .12); font-family: Arial, 'Malgun Gothic', sans-serif; color: #111; font-size: 12px; line-height: 1.5; border-radius: 2px; }
.stage-nav { display: flex; align-items: center; justify-content: center; gap: 14px; width: 100%; max-width: 210mm; margin: 26px auto 6px; }
.nav-btn { border: 1px solid #d3dceb; background: #fff; color: #42506a; font-size: 13px; font-weight: 800; border-radius: 10px; padding: 10px 18px; cursor: pointer; }
.nav-btn:hover:not(:disabled) { background: #f2f6fc; }
.nav-btn.primary { background: #1b64da; border-color: #1b64da; color: #fff; }
.nav-btn.primary:hover:not(:disabled) { background: #1550b8; }
.nav-btn:disabled { opacity: .55; cursor: default; }
.nav-count { font-size: 12px; font-weight: 800; color: #8a97b5; }
</style>
